
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';

import OnBoardingLayout from '@/modules/onboarding/components/layout';
import { CustomersController, ICustomersLoginResponse, ICustomersRegisterRequest } from '@/shared/endpoints/customers';
import { useAppDispatch } from '@/shared/hooks/store';
import { GtmEvent } from '@/shared/types/enums';
import { setRegisterData } from '@/store/slices/onboarding';
import { ROUTES } from '@/shared/constants/routes';
import { IErrorResponse } from '@/shared/types/interfaces';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import decodeqpHook from '@/services/decodeqp';
import { Loading } from '@/shared/ui';

import { triggerSignUpSfWebhook } from './helpers';
import RegisterV2 from './register-v2';
import Register from './register';
import { IParams } from '@/shared/types/helpers';

const RegisterSwitch = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const [registeredEmail, setRegisteredEmail] = useState('');
    const sendDataToGTM = useGTMDispatch();

    const [params, setParams] = useState<{
        paramDecodeLoading: boolean;
        decodedParams: IParams;
    }>({
        paramDecodeLoading: true,
        decodedParams: {},
    });
    useEffect(() => {
        if (searchParams.size) {
            const code = searchParams.get('code')
            if (code) {
                decodeqpHook.post(code).then((res) => {
                    setParams({ paramDecodeLoading: false, decodedParams: res });
                }).catch(() => {
                    setParams({ paramDecodeLoading: false, decodedParams: {} });
                })
            } else {
                setParams({ paramDecodeLoading: false, decodedParams: {} });
            }
        } else {
            setParams({ paramDecodeLoading: false, decodedParams: {} });
        }
    }, [searchParams])


    const isTypeForm = !!searchParams.size;
    const onSubmit = useCallback(
        async (data: ICustomersRegisterRequest) => {
            const { terms, ...rest } = data;

            const firstName = typeof params?.decodedParams?.first_name === 'string'
                ? params?.decodedParams?.first_name.trim()
                : '';
            const lastName = typeof params?.decodedParams?.last_name === 'string'
                ? params?.decodedParams?.last_name.trim()
                : '';

            const res = await dispatch(CustomersController.register(rest)).then(({ meta, payload }) => {
                if (meta.requestStatus === 'fulfilled') {
                    const { id, email, accountSecurity } = payload as ICustomersLoginResponse;
                    triggerSignUpSfWebhook(data.email);
                    sendDataToGTM({ event: GtmEvent.SIGNUP, userId: id, sessionRecording: terms });

                    (firstName || lastName) && dispatch(
                        setRegisterData({
                            firstName,
                            lastName
                        })
                    );

                    const otpDeliverySource = accountSecurity?.source;
                    const otpTarget = accountSecurity?.channel;
                    
                    navigate(ROUTES.CONFIRM_CODE, {
                        state :{
                            email, 
                            otpDeliverySource,
                            otpTarget,
                            variant: 'register',
                            terms
                        }
                    })
                } else {
                    const errorPayload = payload as IErrorResponse
                    const message = errorPayload?.message || '';
                    message.includes('already registered') && setRegisteredEmail(rest.email);
                }
            });
            return res;
        },
        [dispatch, sendDataToGTM, navigate, params?.decodedParams]
    );

    const layoutOutput = useMemo(() => {
        if (params.paramDecodeLoading) {
            return (<div className="flex max-w-full flex-col overflow-hidden">
                <Loading />
            </div>)
        }

        return (
            <>
                {isTypeForm ? <RegisterV2 params={params.decodedParams} onSubmit={onSubmit} /> : <Register onSubmit={onSubmit} />}

                <ConfirmModal
                    open={!!registeredEmail}
                    cancelButtonHidden
                    withIcon
                    title="Email is already registered"
                    confirmText="Continue to Login"
                    onConfirm={() => navigate(ROUTES.LOGIN)}
                >
                    <p data-testid="register-switch-confirm-title" className="font-dmSans text-center text-lg sm-max:text-[1rem] leading-snug">
                        Looks like an account already exists with this email
                    </p>
                    <p data-testid="register-switch-confirm-email" className="font-atkinsonHyperlegible mt-3 sm-max:mt-2 text-center font-bold text-xl sm-max:text-lg">
                        {registeredEmail}
                    </p>
                </ConfirmModal>
            </>
        )
    }, [isTypeForm, navigate, onSubmit, params.paramDecodeLoading, registeredEmail]);

    return (
        <OnBoardingLayout hasFooter={!isTypeForm} spacings={false} ivoryBg withDropdown={!isTypeForm}>
            {layoutOutput}
        </OnBoardingLayout>
    );
};

export default RegisterSwitch;